import React, { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { ProjectFilter } from '../components/ProjectFilter';
import { BlogCard, BlogPost } from '../components/BlogCard';
import { ArrowUpRight, Clock, Calendar } from 'lucide-react';
const categories = ['All', 'Design', 'Development', 'Strategy', 'Culture'];
const featuredPost: BlogPost = {
  id: 1,
  title: 'The Future of Digital Brutalism in 2024',
  excerpt: 'Exploring how raw aesthetics and bold typography are reshaping the modern web landscape. We dive deep into the origins of brutalism and its evolution into a sophisticated design language for forward-thinking brands.',
  category: 'Design',
  author: 'Alex Chen',
  date: 'Mar 15, 2024',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1558655146-d09347e0b7a9?auto=format&fit=crop&w=1600&q=80'
};
const posts: BlogPost[] = [{
  id: 2,
  title: 'Mastering Dark Mode UI Design',
  excerpt: 'Best practices for creating accessible and visually stunning dark interfaces. Learn about contrast ratios, color saturation, and elevation.',
  category: 'Design',
  author: 'Sarah Miller',
  date: 'Mar 10, 2024',
  readTime: '5 min read',
  image: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&w=800&q=80'
}, {
  id: 3,
  title: 'Building Scalable Design Systems',
  excerpt: 'How we organize components and tokens to maintain consistency across large-scale applications.',
  category: 'Development',
  author: 'Mike Ross',
  date: 'Mar 05, 2024',
  readTime: '10 min read',
  image: 'https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?auto=format&fit=crop&w=800&q=80'
}, {
  id: 4,
  title: 'Brand Strategy for Startups',
  excerpt: 'Why early-stage companies need to invest in a solid brand foundation before scaling.',
  category: 'Strategy',
  author: 'Jessica Lee',
  date: 'Feb 28, 2024',
  readTime: '6 min read',
  image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80'
}, {
  id: 5,
  title: 'Remote Culture at KUJODO',
  excerpt: 'How we maintain creativity and collaboration while working from different time zones.',
  category: 'Culture',
  author: 'David Kim',
  date: 'Feb 20, 2024',
  readTime: '4 min read',
  image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80'
}, {
  id: 6,
  title: 'Animation in React with Framer Motion',
  excerpt: 'A technical deep dive into creating smooth, performant animations for the web.',
  category: 'Development',
  author: 'Tom Wilson',
  date: 'Feb 15, 2024',
  readTime: '12 min read',
  image: 'https://images.unsplash.com/photo-1550439062-609e1531270e?auto=format&fit=crop&w=800&q=80'
}, {
  id: 7,
  title: 'Typography Trends to Watch',
  excerpt: 'From kinetic type to variable fonts, here is what is trending in typography this year.',
  category: 'Design',
  author: 'Emma Davis',
  date: 'Feb 10, 2024',
  readTime: '7 min read',
  image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&w=800&q=80'
}, {
  id: 8,
  title: 'The Psychology of Color',
  excerpt: 'Understanding how color choices impact user behavior and brand perception.',
  category: 'Strategy',
  author: 'Chris Martin',
  date: 'Feb 05, 2024',
  readTime: '6 min read',
  image: 'https://images.unsplash.com/photo-1502691876148-a84978e59af8?auto=format&fit=crop&w=800&q=80'
}, {
  id: 9,
  title: 'Sustainable Web Design',
  excerpt: 'Reducing digital carbon footprints through efficient coding and design practices.',
  category: 'Development',
  author: 'Lisa Wang',
  date: 'Jan 30, 2024',
  readTime: '5 min read',
  image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=800&q=80'
}];
export function Blog() {
  const [activeCategory, setActiveCategory] = useState('All');
  const filteredPosts = activeCategory === 'All' ? posts : posts.filter(post => post.category === activeCategory);
  return <div className="min-h-screen bg-black text-white selection:bg-yellow-400 selection:text-black" data-id="element-137">
      <Navigation data-id="element-138" />

      <main className="pt-32 pb-24 px-6" data-id="element-139">
        <div className="max-w-7xl mx-auto" data-id="element-140">
          {/* Header */}
          <div className="mb-16" data-id="element-141">
            <h1 className="text-6xl md:text-8xl font-black tracking-tighter mb-8" data-id="element-142">
              THE <span className="text-yellow-400" data-id="element-143">BLOG</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl leading-relaxed" data-id="element-144">
              Insights, thoughts, and stories from our team. We write about
              design, technology, and the creative process.
            </p>
          </div>

          {/* Featured Post */}
          <div className="mb-24 group relative cursor-pointer" data-id="element-145">
            <div className="relative aspect-[21/9] overflow-hidden mb-8" data-id="element-146">
              <img src={featuredPost.image} alt={featuredPost.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 grayscale group-hover:grayscale-0" data-id="element-147" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80" data-id="element-148" />

              <div className="absolute bottom-0 left-0 p-8 md:p-12 w-full md:w-2/3" data-id="element-149">
                <div className="flex items-center space-x-4 mb-4" data-id="element-150">
                  <span className="bg-yellow-400 text-black text-xs font-bold px-3 py-1 uppercase tracking-wider" data-id="element-151">
                    Featured
                  </span>
                  <span className="text-yellow-400 text-xs font-bold uppercase tracking-wider" data-id="element-152">
                    {featuredPost.category}
                  </span>
                </div>

                <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight group-hover:text-yellow-400 transition-colors" data-id="element-153">
                  {featuredPost.title}
                </h2>

                <p className="text-gray-300 text-lg mb-6 line-clamp-2" data-id="element-154">
                  {featuredPost.excerpt}
                </p>

                <div className="flex items-center text-sm text-gray-400 space-x-6" data-id="element-155">
                  <span className="font-bold text-white" data-id="element-156">
                    {featuredPost.author}
                  </span>
                  <span className="flex items-center" data-id="element-157">
                    <Calendar className="w-4 h-4 mr-2" data-id="element-158" /> {featuredPost.date}
                  </span>
                  <span className="flex items-center" data-id="element-159">
                    <Clock className="w-4 h-4 mr-2" data-id="element-160" /> {featuredPost.readTime}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Filter */}
          <ProjectFilter categories={categories} activeCategory={activeCategory} onSelectCategory={setActiveCategory} data-id="element-161" />

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-162">
            {filteredPosts.map(post => <div key={post.id} className="h-full" data-id="element-163">
                <BlogCard post={post} data-id="element-164" />
              </div>)}
          </div>
        </div>
      </main>

      <Footer data-id="element-165" />
    </div>;
}